import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Layout from '../components/Layout';
import { ServiceCatalogContent } from './ServiceCatalog';
import { RecurrencePatternsContent } from './RecurrencePatterns';

const TABS = [
  { key: 'service_catalog',           label: 'Service Catalog',     icon: '💼' },
  { key: 'recurrence_patterns',       label: 'Recurrence Patterns', icon: '🔁' },
  { key: 'device_types',              label: 'Device Types',        icon: '📦' },
  { key: 'conducive_condition_types', label: 'Conducive Conditions', icon: '⚠️' },
  { key: 'species',                   label: 'Species',             icon: '🐛' },
  { key: 'regulations',               label: 'Regulations',         icon: '📋' },
  { key: 'report_phrases',            label: 'Report Phrases',      icon: '💬' },
  { key: 'license_categories',        label: 'License Categories',  icon: '🪪' },
  { key: 'evidence_types',            label: 'Evidence Types',      icon: '🔍' },
];

const FIELD_LABELS = {
  name: 'Name', category: 'Category', description: 'Description',
  severity_default: 'Severity Default', regulatory_ref: 'Regulatory Ref',
  sort_order: 'Sort Order', common_name: 'Common Name',
  scientific_name: 'Scientific Name', species_code: 'Species Code',
  risk_level: 'Risk Level', source: 'Source', reference_code: 'Reference Code',
  title: 'Title', severity: 'Severity', applies_to: 'Applies To',
  certification_scheme: 'Cert. Scheme', effective_date: 'Effective Date',
  notes: 'Notes', subcategory: 'Subcategory', trigger_condition: 'Trigger Condition',
  phrase_template: 'Phrase Template', language: 'Language', priority: 'Priority',
  state: 'State', code: 'Code', icon: 'Icon',
  pci_category: 'PCI Category',
};

const TEXTAREA_FIELDS = ['description', 'phrase_template', 'full_text', 'notes', 'title'];
const SELECT_FIELDS = {
  severity_default: ['low', 'moderate', 'high', 'critical'],
  risk_level: ['low', 'medium', 'high', 'critical'],
  severity: ['low', 'moderate', 'high', 'critical'],
  applies_to: ['both', 'interior', 'exterior', 'all', 'food_facility', 'pharma'],
  language: ['en', 'es'],
  pci_category: ['rodent', 'insect', 'bird', 'other'],
};

const emptyForm = (fields) => fields.reduce((acc, f) => ({ ...acc, [f]: '' }), {});

const SystemMaintenance = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('device_types');
  const [data, setData] = useState([]);
  const [config, setConfig] = useState({ fields: [], hasActive: false, idCol: 'id' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (tab) => {
    setLoading(true); setError(''); setData([]);
    try {
      const res = await api.get(`/system-maintenance/${tab}`);
      setData(res.data.data);
      setConfig(res.data.config);
    } catch (e) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(activeTab);
    setShowForm(false); setEditRecord(null); setSearch('');
  }, [activeTab, fetchData]);

  const openCreate = () => {
    setEditRecord(null);
    setForm(emptyForm(config.fields));
    setShowForm(true);
  };

  const openEdit = (record) => {
    setEditRecord(record);
    const f = {};
    config.fields.forEach(field => { f[field] = record[field] ?? ''; });
    if (config.hasActive) f.is_active = record.is_active ?? true;
    setForm(f);
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editRecord) {
        const id = editRecord[config.idCol];
        await api.put(`/system-maintenance/${activeTab}/${id}`, form);
      } else {
        await api.post(`/system-maintenance/${activeTab}`, form);
      }
      setShowForm(false);
      fetchData(activeTab);
    } catch (e) {
      alert(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/system-maintenance/${activeTab}/${id}`);
      setDeleteConfirm(null);
      fetchData(activeTab);
    } catch (e) {
      alert(e.response?.data?.message || 'Delete failed');
    }
  };

  const filtered = data.filter(row => {
    if (!search) return true;
    return Object.values(row).some(v => String(v).toLowerCase().includes(search.toLowerCase()));
  });

  const renderField = (field) => {
    if (SELECT_FIELDS[field]) {
      const isPciCat = field === 'pci_category';
      return (
        <div key={field} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <select
            style={styles.input}
            value={form[field] || ''}
            onChange={e => setForm({ ...form, [field]: e.target.value })}
            title={isPciCat ? 'Leave empty to auto-derive from Category' : undefined}
          >
            <option value="">{isPciCat ? '— Auto-derive from Category —' : '— Select —'}</option>
            {SELECT_FIELDS[field].map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          {isPciCat && (
            <span style={{ fontSize: 11, color: '#6B7280', fontStyle: 'italic' }}>
              💡 Used by the PCI engine to group sightings (rodent/insect/bird/other).
              Leave empty and the system will derive it from the Category field.
            </span>
          )}
        </div>
      );
    }
    if (TEXTAREA_FIELDS.includes(field)) {
      return (
        <textarea key={field} style={{ ...styles.input, height: '80px', resize: 'vertical' }}
          value={form[field] || ''}
          onChange={e => setForm({ ...form, [field]: e.target.value })} />
      );
    }
    return (
      <input key={field} style={styles.input} type={field === 'priority' || field === 'sort_order' ? 'number' : 'text'}
        value={form[field] || ''}
        onChange={e => setForm({ ...form, [field]: e.target.value })} />
    );
  };

  const getCellValue = (row, field) => {
    const val = row[field];
    if (val === null || val === undefined) return <span style={{ color: '#bbb' }}>—</span>;
    if (typeof val === 'boolean') return val ? '✅' : '❌';
    if (String(val).length > 60) return String(val).substring(0, 60) + '…';
    return String(val);
  };

  const displayFields = config.fields.slice(0, 5); // show first 5 cols in table

  return (
    <Layout>
      <div style={styles.main}>
        {/* Page header */}
        <div style={styles.pageHeader}>
          <div>
            <h2 style={styles.pageTitle}>⚙️ System Maintenance</h2>
            <p style={styles.pageSubtitle}>Manage internal catalog tables</p>
          </div>
        </div>

        {/* Tabs */}
        <div style={styles.tabBar}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              style={{ ...styles.tab, ...(activeTab === t.key ? styles.tabActive : {}) }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'recurrence_patterns' ? (<RecurrencePatternsContent />) : activeTab === 'service_catalog' ? (
          <ServiceCatalogContent embedded={true} />
        ) : (
        <div style={styles.card}>
          {/* Toolbar */}
          <div style={styles.toolbar}>
            <input style={styles.searchInput} placeholder="🔍 Search..." value={search}
              onChange={e => setSearch(e.target.value)} />
            <span style={styles.countBadge}>{filtered.length} records</span>
            <button style={styles.addBtn} onClick={openCreate}>+ Add New</button>
          </div>

          {loading && <p style={styles.info}>Loading...</p>}
          {error && <p style={styles.errorText}>{error}</p>}

          {/* Table */}
          {!loading && filtered.length > 0 && (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.thead}>
                    <th style={styles.th}>#</th>
                    {displayFields.map(f => <th key={f} style={styles.th}>{FIELD_LABELS[f] || f}</th>)}
                    {config.hasActive && <th style={styles.th}>Active</th>}
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => (
                    <tr key={row[config.idCol]} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                      <td style={{ ...styles.td, color: '#999', fontSize: '12px' }}>{row[config.idCol]}</td>
                      {displayFields.map(f => <td key={f} style={styles.td}>{getCellValue(row, f)}</td>)}
                      {config.hasActive && (
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, backgroundColor: row.is_active ? '#4CAF50' : '#9E9E9E' }}>
                            {row.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      )}
                      <td style={styles.td}>
                        <div style={styles.actions}>
                          <button style={styles.editBtn} onClick={() => openEdit(row)}>✏️ Edit</button>
                          <button style={styles.deleteBtn} onClick={() => setDeleteConfirm(row[config.idCol])}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && filtered.length === 0 && !error && (
            <p style={styles.empty}>No records found{search ? ` for "${search}"` : ''}</p>
          )}
        </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{editRecord ? '✏️ Edit Record' : '➕ New Record'}</h3>
              <button style={styles.closeBtn} onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div style={styles.modalBody}>
              {config.fields.map(field => (
                <div key={field} style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>{FIELD_LABELS[field] || field}</label>
                  {renderField(field)}
                </div>
              ))}
              {config.hasActive && (
                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>Active</label>
                  <select style={styles.input} value={form.is_active === false ? 'false' : 'true'}
                    onChange={e => setForm({ ...form, is_active: e.target.value === 'true' })}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : '💾 Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm !== null && (
        <div style={styles.overlay}>
          <div style={{ ...styles.modal, maxWidth: '400px' }}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>🗑️ Confirm Delete</h3>
            </div>
            <div style={styles.modalBody}>
              <p style={{ color: '#333', margin: 0 }}>Are you sure you want to delete this record? This action cannot be undone.</p>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button style={{ ...styles.saveBtn, backgroundColor: '#C62828' }} onClick={() => handleDelete(deleteConfirm)}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#F5F5F5' },
  header: { backgroundColor: '#2D5C3F', padding: '16px 0', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
  headerContent: { maxWidth: '1400px', margin: '0 auto', padding: '0 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  logo: { color: '#fff', fontSize: '22px', fontWeight: 'bold', margin: 0, cursor: 'pointer' },
  userSection: { display: 'flex', alignItems: 'center', gap: '12px' },
  userName: { color: '#fff', fontWeight: '500', fontSize: '14px' },
  userRole: { color: '#D4AF37', fontSize: '13px' },
  logoutBtn: { backgroundColor: '#D4AF37', color: '#1A3A2A', border: 'none', padding: '7px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
  main: { maxWidth: '1400px', margin: '0 auto', padding: '30px 20px' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  pageTitle: { fontSize: '28px', color: '#2D5C3F', margin: '0 0 4px 0' },
  pageSubtitle: { color: '#666', fontSize: '14px', margin: 0 },
  backBtn: { backgroundColor: '#fff', color: '#2D5C3F', border: '2px solid #2D5C3F', padding: '9px 18px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
  tabBar: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' },
  tab: { padding: '9px 16px', borderRadius: '8px 8px 0 0', border: '1px solid #ddd', backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#555', transition: 'all 0.2s' },
  tabActive: { backgroundColor: '#2D5C3F', color: '#fff', borderColor: '#2D5C3F' },
  card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', overflow: 'hidden' },
  toolbar: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderBottom: '1px solid #eee' },
  searchInput: { flex: 1, padding: '9px 14px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none' },
  countBadge: { backgroundColor: '#E8F5E9', color: '#2D5C3F', padding: '5px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' },
  addBtn: { backgroundColor: '#2D5C3F', color: '#fff', border: 'none', padding: '9px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { backgroundColor: '#2D5C3F' },
  th: { padding: '12px 15px', textAlign: 'left', color: '#fff', fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap' },
  trEven: { backgroundColor: '#fff' },
  trOdd: { backgroundColor: '#FAFAFA' },
  td: { padding: '11px 15px', fontSize: '13px', color: '#333', borderBottom: '1px solid #F0F0F0' },
  badge: { padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', color: '#fff' },
  actions: { display: 'flex', gap: '6px' },
  editBtn: { backgroundColor: '#E3F2FD', color: '#1565C0', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' },
  deleteBtn: { backgroundColor: '#FFEBEE', color: '#C62828', border: 'none', padding: '5px 8px', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' },
  info: { padding: '30px', textAlign: 'center', color: '#666' },
  errorText: { padding: '20px', textAlign: 'center', color: '#C62828' },
  empty: { padding: '40px', textAlign: 'center', color: '#999' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '600px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', margin: '20px' },
  modalHeader: { padding: '20px 24px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { margin: 0, fontSize: '18px', color: '#2D5C3F', fontWeight: '600' },
  closeBtn: { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#666', padding: '4px' },
  modalBody: { padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
  fieldLabel: { fontSize: '13px', fontWeight: '600', color: '#555' },
  input: { padding: '9px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' },
  modalFooter: { padding: '16px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
  cancelBtn: { backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #ddd', padding: '9px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' },
  saveBtn: { backgroundColor: '#2D5C3F', color: '#fff', border: 'none', padding: '9px 24px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' },
};

export default SystemMaintenance;
